import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ethers } from 'ethers';
import PaymentVaultABI from 'abis/PaymentVault';
import PayoutExecutorABI from 'abis/PayoutExecutor';
import RevenueSplitterABI from 'abis/RevenueSplitter';
import { buttonStyles } from '../css/commonStyles';
import {
  alertErrorSx,
  alertSuccessSx,
  colors,
  confirmBtnSx,
  dialogTitleSx,
  fieldBlockStyles,
  fieldLabelStyles,
  formFieldStyles,
  modalPaperStyles,
  newRequestBtnSx,
  pageTitleSx,
  tableCardStyles,
  tableContainerStyles,
  tabHeaderRowSx,
} from '../css/revenueItems.styles';

/** Prompt the wallet to switch to `targetChainId` if needed (EIP-1193). */
async function ensureCorrectChain(targetChainId) {
  if (!window.ethereum || targetChainId === undefined || targetChainId === null || targetChainId === '') {
    return;
  }
  const tid = Number(targetChainId);
  if (Number.isNaN(tid)) return;

  const hexChainId = `0x${tid.toString(16)}`;
  const current = await window.ethereum.request({ method: 'eth_chainId' });
  if (String(current).toLowerCase() === hexChainId.toLowerCase()) return;

  try {
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: hexChainId }],
    });
  } catch (err) {
    if (err?.code === 4902) {
      throw new Error(`Network chain ${tid} is not added to your wallet. Please add it and try again.`);
    }
    if (err?.code === 4001) {
      throw new Error('Please approve switching to the DAO network in your wallet, then try Refresh.');
    }
    throw err;
  }
}

function formatRevenueLoadError(err) {
  if (err?.code === 'CALL_EXCEPTION') {
    return (
      'Could not read this Vault on the current network. Switch your wallet to the DAO chain, then use Refresh. ' +
      'If it still fails, the address may not be a Payment Vault with PayoutExecutor, or you may be on the wrong network.'
    );
  }
  return err?.message || 'Failed to load RevenueSplitter data from chain.';
}

const RevenueSplitterManagement = ({ vaultAddress, chainId }) => {
  const [splitterAddress, setSplitterAddress] = useState('');
  const [activeVersion, setActiveVersion] = useState(null);
  const [rows, setRows] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDaoOperator, setIsDaoOperator] = useState(false);
  const [walletAddr, setWalletAddr] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [lpPct, setLpPct] = useState('');
  const [daoPct, setDaoPct] = useState('');
  const [platformPct, setPlatformPct] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [txPending, setTxPending] = useState(false);
  const [activatingVersion, setActivatingVersion] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const effectiveVault = useMemo(() => {
    const v = (vaultAddress || '').trim();
    if (/^0x[0-9a-fA-F]{40}$/.test(v)) return v;
    return '';
  }, [vaultAddress]);

  const refreshData = useCallback(async () => {
    setLoadError('');
    setSuccessMsg('');
    if (!effectiveVault || !window.ethereum) {
      setSplitterAddress('');
      setRows([]);
      setActiveVersion(null);
      setIsDaoOperator(false);
      return;
    }

    setLoading(true);
    try {
      if (chainId == null || String(chainId).trim() === '') {
        throw new Error('This DAO has no chain configured. Set chainId on the DAO to load the Vault.');
      }

      await ensureCorrectChain(chainId);

      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const bytecode = await provider.getCode(effectiveVault);
      if (!bytecode || bytecode === '0x') {
        throw new Error(
          `No contract bytecode at this Vault address on chain ${Number(chainId)}. ` +
            'Confirm the Vault address and that your wallet is on the correct network.',
        );
      }

      const vault = new ethers.Contract(effectiveVault, PaymentVaultABI, provider);
      const payoutExecAddr = await vault.payoutExecutor();
      const payoutExec = new ethers.Contract(payoutExecAddr, PayoutExecutorABI, provider);
      const splitterAddr = await payoutExec.splitter();
      setSplitterAddress(splitterAddr);

      const splitter = new ethers.Contract(splitterAddr, RevenueSplitterABI, provider);
      const daoOperatorRole = await splitter.DAO_OPERATOR_ROLE();
      const [av, all] = await Promise.all([splitter.activeVersion(), splitter.getAllSplitConfigs()]);

      setActiveVersion(av.toNumber());

      const versions = all[0].map((x) => x.toNumber());
      const lpList = all[1].map((x) => x.toNumber());
      const daoList = all[2].map((x) => x.toNumber());
      const platList = all[3].map((x) => x.toNumber());

      const mapped = versions.map((ver, i) => ({
        version: ver,
        lp: lpList[i],
        dao: daoList[i],
        platform: platList[i],
      }));
      setRows(mapped);

      const selected = await window.ethereum.request({ method: 'eth_accounts' });
      const account = selected?.[0];
      setWalletAddr(account || '');
      if (account) {
        const ok = await splitter.hasRole(daoOperatorRole, account);
        setIsDaoOperator(ok);
      } else {
        setIsDaoOperator(false);
      }
    } catch (err) {
      console.error(err);
      setSplitterAddress('');
      setRows([]);
      setActiveVersion(null);
      setLoadError(formatRevenueLoadError(err));
    } finally {
      setLoading(false);
    }
  }, [effectiveVault, chainId]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  useEffect(() => {
    if (!window.ethereum) return undefined;
    const onAccounts = () => refreshData();
    const onChain = () => refreshData();
    window.ethereum.on('accountsChanged', onAccounts);
    window.ethereum.on('chainChanged', onChain);
    return () => {
      window.ethereum.removeListener('accountsChanged', onAccounts);
      window.ethereum.removeListener('chainChanged', onChain);
    };
  }, [refreshData]);

  const handleOpenDialog = () => {
    setDialogError('');
    setLpPct('');
    setDaoPct('');
    setPlatformPct('');
    setDialogOpen(true);
  };

  const handleCreateVersion = async () => {
    setDialogError('');
    const lp = parseInt(lpPct, 10);
    const dao = parseInt(daoPct, 10);
    const plat = parseInt(platformPct, 10);
    if ([lp, dao, plat].some((n) => Number.isNaN(n) || n < 0)) {
      setDialogError('Enter valid non-negative integers for all shares.');
      return;
    }
    if (lp + dao + plat !== 100) {
      setDialogError('LP + DAO Treasury + Platform must equal 100.');
      return;
    }

    if (!splitterAddress || !window.ethereum) return;
    setSubmitting(true);
    try {
      await ensureCorrectChain(chainId);
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const signer = provider.getSigner();
      const splitter = new ethers.Contract(splitterAddress, RevenueSplitterABI, signer);
      setTxPending(true);
      const tx = await splitter.setSplitConfig(lp, dao, plat);
      await tx.wait();
      setDialogOpen(false);
      setSuccessMsg('New split configuration created and set as the default.');
      await refreshData();
    } catch (err) {
      setDialogError(err?.reason || err?.message || 'Transaction failed.');
    } finally {
      setSubmitting(false);
      setTxPending(false);
    }
  };

  const handleSetDefaultVersion = async (version) => {
    if (!splitterAddress || !window.ethereum) return;
    setActivatingVersion(version);
    setLoadError('');
    setSuccessMsg('');
    try {
      await ensureCorrectChain(chainId);
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const signer = provider.getSigner();
      const splitter = new ethers.Contract(splitterAddress, RevenueSplitterABI, signer);
      setTxPending(true);
      const tx = await splitter.setActiveVersion(version);
      await tx.wait();
      setSuccessMsg(`Default split version set to ${version}.`);
      await refreshData();
    } catch (err) {
      setLoadError(err?.reason || err?.message || 'Transaction failed.');
    } finally {
      setActivatingVersion(null);
      setTxPending(false);
    }
  };

  if (!effectiveVault) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography sx={{ ...pageTitleSx, fontSize: '1.35rem', mb: 1 }}>Revenue Splitter</Typography>
        <Typography sx={{ color: colors.textSecondary, fontFamily: 'Questrial, sans-serif' }}>
          Configure a valid Vault address above to load the RevenueSplitter contract.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4, width: '100%' }}>
      <Typography sx={{ ...pageTitleSx, fontSize: '1.35rem', mb: 1 }}>Revenue Splitter</Typography>

      {loadError && (
        <Alert severity="error" sx={{ ...alertErrorSx, mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" sx={{ ...alertSuccessSx, mb: 2 }}>
          {successMsg}
        </Alert>
      )}

      {!loading && splitterAddress && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              gap: 1.5,
              rowGap: 0.5,
            }}
          >
            <Typography sx={{ color: colors.textSecondary, fontSize: '0.9rem', flexShrink: 0 }}>
              Revenue Splitter Address:
            </Typography>
            <Typography
              sx={{
                fontFamily: 'monospace',
                color: colors.textPrimary,
                fontSize: '0.9rem',
                wordBreak: 'break-all',
              }}
            >
              {splitterAddress}
            </Typography>
          </Box>
          {walletAddr && (
            <Typography
              sx={{
                color: isDaoOperator ? 'rgba(76, 175, 80, 0.95)' : '#eab308',
                mt: 1,
                fontSize: '0.9rem',
                fontFamily: 'Questrial, sans-serif',
              }}
            >
              {isDaoOperator
                ? 'Your wallet has DAO_OPERATOR_ROLE on this RevenueSplitter.'
                : 'Your wallet does not have DAO_OPERATOR_ROLE — connect an authorized wallet to submit transactions.'}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ ...tabHeaderRowSx, mt: 2 }}>
        <Typography sx={{ color: colors.textPrimary, fontSize: '1.1rem', fontFamily: 'Questrial, sans-serif' }}>
          Split configs by version
        </Typography>
        <Button
          variant="contained"
          onClick={handleOpenDialog}
          disabled={loading || !splitterAddress || txPending || !isDaoOperator}
          sx={{ ...buttonStyles, ...newRequestBtnSx }}
        >
          New version
        </Button>
      </Box>

      <Box sx={tableCardStyles}>
        <TableContainer component={Box} sx={{ ...tableContainerStyles, '& .MuiTable-root': { minWidth: 720 } }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>LP share</TableCell>
                <TableCell>DAO treasury</TableCell>
                <TableCell>Platform</TableCell>
                    <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={22} sx={{ color: colors.accent }} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && !splitterAddress && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5, color: colors.textSecondary }}>
                    Could not resolve RevenueSplitter for this Vault.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                splitterAddress &&
                rows.map((r) => (
                  <TableRow key={r.version}>
                    <TableCell>{r.version}</TableCell>
                    <TableCell>{r.lp}%</TableCell>
                    <TableCell>{r.dao}%</TableCell>
                    <TableCell>{r.platform}%</TableCell>
                    <TableCell>
                      {r.version === activeVersion ? (
                        <Typography component="span" sx={{ color: colors.accent }}>
                          Default
                        </Typography>
                      ) : (
                        <Typography component="span" sx={{ color: colors.textSecondary }}>
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {r.version !== activeVersion && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!isDaoOperator || txPending || activatingVersion === r.version}
                          onClick={() => handleSetDefaultVersion(r.version)}
                          sx={{
                            color: colors.accent,
                            borderColor: colors.accent,
                            textTransform: 'none',
                            fontFamily: 'Questrial, sans-serif',
                          }}
                        >
                          {activatingVersion === r.version ? (
                            <CircularProgress size={16} sx={{ color: colors.accent }} />
                          ) : (
                            'Set as default'
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              {!loading && splitterAddress && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5, color: colors.textSecondary }}>
                    No versions returned from contract.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => !submitting && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: modalPaperStyles }}
      >
        <DialogTitle sx={dialogTitleSx}>New split version</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: colors.textSecondary, mb: 2, fontFamily: 'Questrial, sans-serif' }}>
            LP + DAO treasury + platform must total 100. Creates a new version and sets it as the default.
          </Typography>
          {dialogError && (
            <Alert severity="error" sx={alertErrorSx}>
              {dialogError}
            </Alert>
          )}
          <Box sx={fieldBlockStyles}>
            <Typography sx={fieldLabelStyles}>LP share (%)</Typography>
            <TextField
              type="number"
              value={lpPct}
              onChange={(e) => setLpPct(e.target.value)}
              fullWidth
              sx={formFieldStyles}
              inputProps={{ min: 0, max: 100 }}
            />
          </Box>
          <Box sx={fieldBlockStyles}>
            <Typography sx={fieldLabelStyles}>DAO treasury (%)</Typography>
            <TextField
              type="number"
              value={daoPct}
              onChange={(e) => setDaoPct(e.target.value)}
              fullWidth
              sx={formFieldStyles}
              inputProps={{ min: 0, max: 100 }}
            />
          </Box>
          <Box sx={fieldBlockStyles}>
            <Typography sx={fieldLabelStyles}>Platform (%)</Typography>
            <TextField
              type="number"
              value={platformPct}
              onChange={(e) => setPlatformPct(e.target.value)}
              fullWidth
              sx={formFieldStyles}
              inputProps={{ min: 0, max: 100 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => setDialogOpen(false)}
              disabled={submitting}
              sx={{ color: colors.textSecondary, borderColor: colors.border, textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateVersion}
              disabled={submitting}
              sx={{ ...buttonStyles, ...confirmBtnSx }}
            >
              {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Submit on-chain'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RevenueSplitterManagement;
